import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Switch } from '@headlessui/react'
import classnames from 'classnames'
import { Field, Form, Formik } from 'formik'
import Button from '../../../../components/Form/Button'
import Select from '../../../../components/Form/Select'
import Modal from '../../../../components/Modal'
import { CustomContentModal } from '../../../../components/CustomContentModal'
import { pushToast } from '../../../../components/Toaster/Toaster.slice'
import { downloadDocumentApi, getAutopayAgreementApi, updateAutopayApi } from '../../../../apis/apis'
import { getApiErrorMessage } from '../../../../apis/apiError'
import { AccountManager, ServiceAccount } from '../../../../apis/types'
import { useAppDispatch, useAppSelector } from '../../../../shared/redux/hooks'
import { getTenant } from '../Tenants.slice'
import { formatFileSize } from '../../../../util/string'

const AUTOPAY_METHOD_TYPES = ['CREDIT_CARD', 'CHECKING', 'SAVINGS']
const AGREEMENT_HELPER = 'A signed Payment Plan Agreement is required.'
const MAX_FILE_SIZE = 10 * 1024 * 1024

type Pending = 'enroll' | 'off' | null
type AgreementStatus = 'idle' | 'loading' | 'missing' | 'present' | 'error'

export interface AutopayMethodOption {
  id: string
  label: string
}

export const autopayMethodOptions = (manager: AccountManager | undefined): AutopayMethodOption[] =>
  (manager?.paymentMethods ?? [])
    .filter((method) => AUTOPAY_METHOD_TYPES.includes(method.paymentMethodType))
    .sort((a, b) => Number(b.default === true) - Number(a.default === true))
    .map((method) => ({
      id: method.id,
      label: `${method.providerName} (••••${method.last4})${method.default === true ? ' — Primary method' : ''}`,
    }))

const firstMethodId = (manager: AccountManager | undefined): string => autopayMethodOptions(manager)[0]?.id ?? ''

const managerDisplayName = (manager: AccountManager | undefined): string => {
  const first = manager?.profile?.firstName?.trim() ?? ''
  const last = manager?.profile?.lastName?.trim() ?? ''
  const name = `${first} ${last}`.trim()
  return name !== '' ? name : (manager?.email ?? '')
}

const methodChargeLabel = (manager: AccountManager | undefined, paymentMethodId: string): string => {
  const method = manager?.paymentMethods.find((pm) => pm.id === paymentMethodId)
  if (method == null) return ''
  return `${method.providerName} (••••${method.last4})`
}

export interface AutopayControlProps {
  serviceAccount: ServiceAccount
}

const AutopayControl: React.FC<AutopayControlProps> = ({ serviceAccount }) => {
  const dispatch = useAppDispatch()
  const tenantID = useAppSelector((state) => state.tenants.tenant?.id)
  const { permissions } = useAppSelector((state) => state.app)
  const [pending, setPending] = useState<Pending>(null)
  const [agreementStatus, setAgreementStatus] = useState<AgreementStatus>('idle')
  const [agreementDocumentCuid, setAgreementDocumentCuid] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [isViewingAgreement, setIsViewingAgreement] = useState(false)
  const [confirmModal, setConfirmModal] = useState<React.ReactElement | false>(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const confirmRejectRef = useRef<((error: Error) => void) | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const enabled = serviceAccount.paymentPreference.autopay.enabled
  const [checked, setChecked] = useState(enabled)
  const currentMethodId = serviceAccount.paymentPreference.paymentMethodId
  const managers = serviceAccount.accountManagers

  useEffect(() => {
    setChecked(enabled)
  }, [enabled, serviceAccount.id])

  useEffect(() => {
    if (pending !== 'enroll') return
    let cancelled = false
    setAgreementStatus('loading')
    setAgreementDocumentCuid(null)
    setSelectedFile(null)
    setIsDragOver(false)
    void getAutopayAgreementApi(serviceAccount.id)
      .then((data) => {
        if (cancelled) return
        setAgreementDocumentCuid(typeof data.documentCuid === 'string' && data.documentCuid !== '' ? data.documentCuid : null)
        setAgreementStatus(data.alreadyPresent ? 'present' : 'missing')
      })
      .catch(() => {
        if (cancelled) return
        setAgreementStatus('error')
        void dispatch(pushToast({ type: 'error', message: 'Failed to load the Payment Plan Agreement' }))
      })
    return () => {
      cancelled = true
    }
  }, [pending, serviceAccount.id, dispatch])

  const close = (): void => {
    confirmRejectRef.current?.(new Error('closed'))
    confirmRejectRef.current = null
    setConfirmModal(false)
    setPending(null)
    setAgreementStatus('idle')
    setAgreementDocumentCuid(null)
    setSelectedFile(null)
    setIsDragOver(false)
  }

  const validateFile = useCallback(
    (file: File): boolean => {
      const isPdf = file.type === 'application/pdf' || (!file.type && file.name.toLowerCase().endsWith('.pdf'))
      if (!isPdf) {
        void dispatch(pushToast({ message: 'Only PDF files are allowed', type: 'error' }))
        return false
      }
      if (file.size > MAX_FILE_SIZE) {
        void dispatch(pushToast({ message: 'File size must be less than 10 MB', type: 'error' }))
        return false
      }
      return true
    },
    [dispatch]
  )

  const handleFileSelect = useCallback(
    (file: File) => {
      if (validateFile(file)) {
        setSelectedFile(file)
      }
    },
    [validateFile]
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) handleFileSelect(file)
      if (fileInputRef.current) fileInputRef.current.value = ''
    },
    [handleFileSelect]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setIsDragOver(false)
      const file = e.dataTransfer.files?.[0]
      if (file) handleFileSelect(file)
    },
    [handleFileSelect]
  )

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (e.currentTarget.contains(e.relatedTarget as Node)) return
    setIsDragOver(false)
  }, [])

  const viewAgreement = async (): Promise<void> => {
    if (agreementDocumentCuid == null || isViewingAgreement) return
    setIsViewingAgreement(true)
    try {
      const { data, filename } = await downloadDocumentApi(serviceAccount.id, agreementDocumentCuid)
      const blob = new Blob([data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
      void dispatch(pushToast({ message: `Opened ${filename}`, type: 'success' }))
    } catch {
      void dispatch(pushToast({ message: 'Failed to open document', type: 'error' }))
    } finally {
      setIsViewingAgreement(false)
    }
  }

  if (!(permissions?.includes('payops:update:autopay') ?? false)) return null

  const userOptions = managers.reduce(
    (a, v) => ({
      ...a,
      [v.id]: `${v.profile?.firstName !== undefined ? `${v.profile?.firstName} ` : ''} ${
        v.profile?.lastName !== undefined ? `${v.profile?.lastName} — ` : ''
      } ${v.email}`,
    }),
    {} as Record<string, string>
  )

  const methodOwnerId = managers.find((m) => m.paymentMethods.some((pm) => pm.id === currentMethodId))?.id ?? ''
  const lockTurnOffUser = enabled && methodOwnerId !== ''
  const defaultUserId =
    methodOwnerId ||
    managers.find((m) => autopayMethodOptions(m).length > 0)?.id ||
    managers[0]?.id ||
    ''

  const patchAutopay = async (
    userId: string,
    nextEnabled: boolean,
    paymentMethodId: string | null | undefined,
    file?: File | null
  ): Promise<boolean> => {
    if (isUpdating) return false
    setIsUpdating(true)
    try {
      const payload = {
        tenantAccountId: serviceAccount.id,
        userId,
        enabled: nextEnabled,
        ...(paymentMethodId != null && paymentMethodId !== '' ? { paymentMethodId } : {}),
      }
      if (nextEnabled && file != null) {
        await updateAutopayApi(payload, file)
      } else {
        await updateAutopayApi(payload)
      }
      setChecked(nextEnabled)
      void dispatch(pushToast({ message: nextEnabled ? 'AutoPay turned on' : 'AutoPay turned off' }))
      if (tenantID !== undefined) {
        void dispatch(getTenant(tenantID))
      }
      close()
      return true
    } catch (e) {
      void dispatch(
        pushToast({
          type: 'error',
          message: 'Error updating AutoPay',
          description: getApiErrorMessage(e, e instanceof Error ? e.message : 'Unknown error'),
        })
      )
      return false
    } finally {
      setIsUpdating(false)
    }
  }

  const confirmAction = async (title: string, message: string): Promise<boolean> => {
    try {
      await new Promise((resolve, reject) => {
        const rejectConfirm = (error: Error): void => {
          confirmRejectRef.current = null
          reject(error)
        }
        confirmRejectRef.current = rejectConfirm
        setConfirmModal(
          <Modal
            resolve={(value) => {
              confirmRejectRef.current = null
              resolve(value)
            }}
            reject={rejectConfirm}
            title={title}
            message={message}
          />
        )
      })
      setConfirmModal(false)
      return true
    } catch {
      setConfirmModal(false)
      return false
    }
  }

  const agreementReady = agreementStatus === 'present' || selectedFile != null

  return (
    <>
      <Switch.Group as="div" className="flex items-center gap-2">
        <Switch.Label as="span" className="text-sm font-medium text-slate-600 dark:text-slate-50" passive>
          AutoPay
        </Switch.Label>
        <Switch
          checked={checked}
          disabled={pending != null || isUpdating}
          onChange={(next) => {
            if (checked === next || pending != null || isUpdating) return
            setPending(next ? 'enroll' : 'off')
          }}
          className={classnames(
            checked ? 'bg-indigo-600' : 'bg-slate-200 dark:bg-slate-600',
            'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-wait disabled:opacity-60'
          )}
        >
          <span
            aria-hidden="true"
            className={classnames(
              checked ? 'translate-x-4' : 'translate-x-0',
              'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
            )}
          />
        </Switch>
      </Switch.Group>

      <CustomContentModal isOpen={pending === 'enroll'} onClose={close}>
        <div className="w-full max-w-lg rounded-lg bg-white p-6 text-left shadow-xl dark:bg-slate-800">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Confirm AutoPay preference with consent</h3>
          </div>

          {managers.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400">Please add an account manager to this service account before changing AutoPay.</p>
          ) : (
            <Formik
              enableReinitialize
              initialValues={{ user: defaultUserId, paymentMethodId: firstMethodId(managers.find((m) => m.id === defaultUserId)) }}
              onSubmit={async (values) => {
                const manager = managers.find((m) => m.id === values.user)
                const methodLabel = autopayMethodOptions(manager).find((o) => o.id === values.paymentMethodId)?.label ?? ''
                const confirmed = await confirmAction(
                  'Enroll in AutoPay?',
                  `Charge ${methodLabel} automatically for ${serviceAccount.kingAccountNumber}, on behalf of ${manager?.email ?? ''}?`
                )
                if (!confirmed) return
                await patchAutopay(
                  values.user,
                  true,
                  values.paymentMethodId,
                  agreementStatus === 'present' ? undefined : selectedFile
                )
              }}
            >
              {({ values, setValues, isSubmitting }) => {
                const manager = managers.find((m) => m.id === values.user)
                const methodOptions = autopayMethodOptions(manager)
                const canEnroll = agreementReady && methodOptions.some((option) => option.id === values.paymentMethodId)
                const chargeLabel = methodChargeLabel(manager, values.paymentMethodId)
                const onFilePhrase = agreementStatus === 'present' ? ', with a signed Payment Plan Agreement on file' : ''
                const authorization =
                  chargeLabel === ''
                    ? ''
                    : `You are turning on AutoPay on behalf of ${managerDisplayName(manager)} (${manager?.email ?? ''}) for this service account${onFilePhrase}. King Energy will charge ${chargeLabel} on that wallet for later invoices, including different amounts, and may credit that method to correct an erroneous debit.`

                return (
                  <Form>
                    <div className="grid grid-cols-1 gap-3">
                      <Field
                        component={Select}
                        name="user"
                        label="On behalf of"
                        options={userOptions}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                          void setValues({
                            user: e.target.value,
                            paymentMethodId: firstMethodId(managers.find((m) => m.id === e.target.value)),
                          })
                        }}
                      />

                      {methodOptions.length > 0 ? (
                        <Field
                          component={Select}
                          name="paymentMethodId"
                          label="Saved payment method"
                          description={`On ${manager?.email ?? ''}'s wallet`}
                          options={Object.fromEntries(methodOptions.map((o) => [o.id, o.label]))}
                        />
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          This account manager has no active credit card, checking, or savings method. The tenant adds one in the customer portal.
                        </p>
                      )}

                      {chargeLabel !== '' && (
                        <>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{authorization}</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">AutoPay will be turned on for this service account:</p>
                          <div className="rounded-md border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-slate-900">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{serviceAccount.kingAccountName}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{serviceAccount.kingAccountNumber}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{chargeLabel}</p>
                          </div>
                        </>
                      )}

                      {agreementStatus === 'loading' && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">Checking for the Payment Plan Agreement…</p>
                      )}
                      {agreementStatus === 'present' && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Signed Payment Plan Agreement is on file.
                          {agreementDocumentCuid != null && (
                            <>
                              {' '}
                              <button
                                type="button"
                                className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
                                disabled={isViewingAgreement}
                                onClick={() => {
                                  void viewAgreement()
                                }}
                              >
                                View
                              </button>
                            </>
                          )}
                        </p>
                      )}
                      {agreementStatus === 'missing' && (
                        <div>
                          <label
                            htmlFor="autopay-agreement-file"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
                          >
                            Payment Plan Agreement (PDF)
                            <span className="ml-1 text-sm text-red-500 dark:text-red-500">*</span>
                          </label>
                          <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">{AGREEMENT_HELPER}</p>
                          <input
                            ref={fileInputRef}
                            id="autopay-agreement-file"
                            type="file"
                            accept=".pdf"
                            onChange={handleInputChange}
                            className="hidden"
                          />

                          {selectedFile == null ? (
                            <div
                              onClick={() => fileInputRef.current?.click()}
                              onDrop={handleDrop}
                              onDragOver={handleDragOver}
                              onDragLeave={handleDragLeave}
                              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                                isDragOver
                                  ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 ring-2 ring-indigo-200 dark:ring-indigo-800'
                                  : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                              }`}
                            >
                              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-indigo-50 dark:bg-indigo-900/30">
                                <svg
                                  className="h-7 w-7 text-indigo-500 dark:text-indigo-400"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={1.5}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z"
                                  />
                                </svg>
                              </div>
                              <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                                Drag and drop your file here, or{' '}
                                <span className="text-indigo-600 dark:text-indigo-400 font-medium">browse</span>
                              </p>
                              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">PDF only, max 10 MB</p>
                            </div>
                          ) : (
                            <div className="bg-white dark:bg-slate-900 rounded-md px-3 py-2 border border-gray-200 dark:border-gray-700 flex items-center justify-between">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{selectedFile.name}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{formatFileSize(selectedFile.size)}</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => setSelectedFile(null)}
                                disabled={isUpdating}
                                aria-label="Remove file"
                                className="ml-2 flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      {agreementStatus === 'error' && (
                        <p className="text-sm text-red-600 dark:text-red-400">
                          Could not check for the Payment Plan Agreement. Close this dialog and try again.
                        </p>
                      )}

                      <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <Button type="button" color="slate" onClick={close}>
                          Cancel
                        </Button>
                        <Button type="submit" color="indigo" disabled={isSubmitting || isUpdating || !canEnroll}>
                          Enroll in AutoPay
                        </Button>
                      </div>
                    </div>
                  </Form>
                )
              }}
            </Formik>
          )}
        </div>
      </CustomContentModal>

      <CustomContentModal isOpen={pending === 'off'} onClose={close}>
        <div className="w-full max-w-lg rounded-lg bg-white p-6 text-left shadow-xl dark:bg-slate-800">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Turn off AutoPay</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Stop charging this account automatically.</p>
          </div>
          {managers.length === 0 || defaultUserId === '' ? (
            <p className="text-gray-500 dark:text-gray-400">Please add an account manager to this service account before changing AutoPay.</p>
          ) : (
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button type="button" color="slate" onClick={close}>
                Cancel
              </Button>
              <Button
                type="button"
                color="red"
                disabled={isUpdating}
                onClick={async () => {
                  if (isUpdating) return
                  const confirmed = await confirmAction(
                    'Turn off AutoPay?',
                    `Stop automatic charges for ${serviceAccount.kingAccountNumber}?`
                  )
                  if (!confirmed) return
                  const userId = lockTurnOffUser ? methodOwnerId : defaultUserId
                  if (userId === '') return
                  await patchAutopay(userId, false, currentMethodId)
                }}
              >
                Turn off AutoPay
              </Button>
            </div>
          )}
        </div>
      </CustomContentModal>
      {confirmModal}
    </>
  )
}

export default AutopayControl
